import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BrandMark } from "../components/Brand";
import { api } from "../lib/api";
import { saveAttorney } from "../lib/attorneyAuth";
import { Loader2, Eye, AlertTriangle } from "lucide-react";

/**
 * Admin-direct impersonation bootstrap.
 *
 * Reads `firm` (and optional `user`) from the URL hash, saves them to
 * localStorage as the active impersonation context, then redirects into the
 * firm portal. From there, every /portal/* request goes through the axios
 * interceptor which auto-injects the admin Bearer + X-Impersonate-Firm header.
 *
 * This intentionally does NOT call the backend impersonate endpoint — bypassing
 * the attorney_session row means the flow is immune to demo resets, browser
 * cache mismatches, and stale session rows.
 */
export default function ImpersonateBoot() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const hash = window.location.hash || window.location.search || "";
    const firmMatch = hash.match(/firm=([^&]+)/);
    const userMatch = hash.match(/user=([^&]+)/);
    const firmId = firmMatch ? decodeURIComponent(firmMatch[1]) : null;
    const targetUser = userMatch ? decodeURIComponent(userMatch[1]) : null;

    if (!firmId) {
      setError("Missing firm context — please re-click 'View portal' from /admin/firms.");
      return;
    }

    const adminToken = (() => { try { return JSON.parse(localStorage.getItem("instabridge_admin"))?.access_token; } catch { return null; } })();
    if (!adminToken) {
      setError("You're not logged in as an admin in this browser. Please sign in at /login first.");
      return;
    }

    (async () => {
      try {
        // Persist the impersonation context BEFORE calling /portal/me so the
        // axios interceptor attaches X-Impersonate-Firm on the first request.
        saveAttorney({
          user: { email: targetUser || "admin", name: "Admin · impersonator", role: "attorney" },
          firm: { firm_id: firmId },
          session_token: null,
          impersonate_firm_id: firmId,
          impersonation: { is_impersonation: true, admin_email: "(loading…)" },
        });
        const meRes = await api.get("/portal/me");
        const me = meRes.data;
        saveAttorney({
          user: { ...me.user, role: "attorney" },
          firm: me.firm,
          session_token: null,
          impersonate_firm_id: firmId,
          impersonation: me.impersonation || { is_impersonation: true },
        });
        window.history.replaceState({}, "", "/portal/impersonate");
        navigate("/attorney/portal", { replace: true });
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Impersonation failed.";
        setError(typeof detail === "string" ? detail : "Impersonation failed.");
      }
    })();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#A16207] to-[#78350F] text-white flex flex-col items-center justify-center px-6" data-testid="impersonate-boot">
      <div className="mb-8 opacity-90"><BrandMark inverted /></div>
      {!error ? (
        <div className="flex flex-col items-center text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center mb-5">
            <Loader2 className="w-7 h-7 text-white animate-spin" />
          </div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#FDE68A] mb-3">
            <Eye className="w-3.5 h-3.5" /> Admin impersonation
          </div>
          <h1 className="font-display text-3xl font-semibold mb-2">Opening firm portal…</h1>
          <p className="text-white/70 text-sm">You'll be viewing the portal exactly as the firm would. A banner stays visible the whole time.</p>
        </div>
      ) : (
        <div className="flex flex-col items-center text-center max-w-md" data-testid="impersonate-error">
          <div className="w-16 h-16 rounded-2xl bg-[#7F1D1D]/40 border border-[#FCA5A5]/60 flex items-center justify-center mb-5">
            <AlertTriangle className="w-7 h-7 text-[#FCA5A5]" />
          </div>
          <h1 className="font-display text-3xl font-semibold mb-2">Could not start impersonation</h1>
          <p className="text-white/70 text-sm mb-6">{error}</p>
          <button onClick={() => navigate("/admin")} className="px-4 h-10 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm font-medium">Back to admin</button>
        </div>
      )}
    </div>
  );
}
